import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../stores/app-store';
import { useAuthStore } from '../stores/auth-store';
import { useTheme } from '../hooks/use-theme';
import {
  Download,
  Upload,
  Trash2,
  Keyboard,
  Sun,
  Moon,
  Palette,
  Monitor,
  Type,
  Save,
  Database,
  Info,
  Key,
  CheckCircle2,
  Eye,
  EyeOff,
} from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { cn } from '../lib/utils';
import { notify, toast } from '../lib/toast';

type SectionId = 'appearance' | 'editor' | 'autosave' | 'shortcuts' | 'data' | 'integrations' | 'about';

export function SettingsView() {
  const { t, i18n } = useTranslation();
  const { settings, updateSettings, exportAllData, importData, clearAllData } = useAppStore();
  const { user, setOpenAIKey, clearOpenAIKey } = useAuthStore();
  const { setTheme } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [section, setSection] = useState<SectionId>('appearance');

  // BYOK state
  const [byokKey, setByokKey] = useState('');
  const [byokShowKey, setByokShowKey] = useState(false);
  const [byokSaving, setByokSaving] = useState(false);
  const [byokRemoving, setByokRemoving] = useState(false);

  const handleLanguageChange = (lang: string) => i18n.changeLanguage(lang);

  const handleExportData = () => {
    const data = exportAllData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `articulat-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      const success = importData(content);
      if (success) notify.success(t('common.success'));
      else notify.error(t('errors.importFailed'));
    };
    reader.readAsText(file);
  };

  const handleClearData = () => {
    toast(t('settings.clearDataConfirm'), {
      action: {
        label: t('common.confirm'),
        onClick: () => {
          clearAllData();
          notify.success(t('common.success'));
        },
      },
      cancel: { label: t('common.cancel'), onClick: () => {} },
      duration: 10000,
    });
  };

  const themeOptions = [
    { value: 'light', icon: Sun, label: t('settings.light') },
    { value: 'dark', icon: Moon, label: t('settings.dark') },
    { value: 'sepia', icon: Palette, label: t('settings.sepia') },
    { value: 'system', icon: Monitor, label: t('settings.system') },
  ] as const;

  const handleSaveByokKey = async () => {
    if (!byokKey.trim()) return;
    setByokSaving(true);
    const ok = await setOpenAIKey(byokKey.trim());
    setByokSaving(false);
    if (ok) {
      setByokKey('');
      notify.success(t('settingsView.byokSaveSuccess', 'Chave da OpenAI salva com sucesso'));
    } else {
      notify.error(t('settingsView.byokSaveError', 'Não foi possível salvar a chave. Verifique o formato (deve começar com sk-).'));
    }
  };

  const handleRemoveByokKey = async () => {
    toast(t('settingsView.byokRemoveConfirm', 'Remover sua chave da OpenAI?'), {
      action: {
        label: t('settingsView.remove', 'Remover'),
        onClick: async () => {
          setByokRemoving(true);
          const ok = await clearOpenAIKey();
          setByokRemoving(false);
          if (ok) notify.success(t('settingsView.byokRemoveSuccess', 'Chave removida — usando cota do plano'));
        },
      },
      cancel: { label: t('common.cancel'), onClick: () => {} },
      duration: 8000,
    });
  };

  const sections: { id: SectionId; label: string; icon: typeof Palette }[] = [
    { id: 'appearance', label: t('settings.appearance', 'Aparência'), icon: Palette },
    { id: 'editor', label: t('settings.editor'), icon: Type },
    { id: 'autosave', label: t('settings.autoSave'), icon: Save },
    { id: 'shortcuts', label: t('settings.keyboard'), icon: Keyboard },
    { id: 'data', label: t('settings.backup'), icon: Database },
    { id: 'integrations', label: t('settingsView.integrations', 'Integrações'), icon: Key },
    { id: 'about', label: t('settings.about'), icon: Info },
  ];

  return (
    <div className="flex-1 overflow-y-auto [-webkit-overflow-scrolling:touch]">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-10 md:py-10">

        {/* Page header — mock "scr-ajustes": mono eyebrow + serif headline */}
        <div className="mb-6 border-b border-border pb-5 md:mb-8 md:pb-6">
          <p className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-muted-foreground">
            {t('settings.title')}
          </p>
          <h1 className="mt-1.5 font-serif text-[22px] font-medium leading-tight text-foreground">
            {t('settings.subtitle', 'Gerencie preferências, editor e dados')}
          </h1>
        </div>

        <div className="md:grid md:grid-cols-[220px_minmax(0,1fr)] md:gap-10 md:items-start">

          {/* Section nav — sticky sidebar on desktop, horizontal scroll on mobile */}
          <nav
            className={cn(
              'mb-6 flex gap-1.5 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4',
              'md:mx-0 md:mb-0 md:flex-col md:gap-1 md:overflow-visible md:px-0 md:pb-0',
              'md:sticky md:top-6 md:self-start'
            )}
          >
            {sections.map(({ id, label, icon: Icon }) => {
              const active = section === id;
              return (
                <button
                  key={id}
                  onClick={() => setSection(id)}
                  className={cn(
                    'flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-xl px-3 py-2.5 text-xs font-medium',
                    'transition-all duration-150',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1',
                    active
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground active:bg-muted/80',
                    'md:w-full'
                  )}
                >
                  {/* Icon pill */}
                  <span
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors duration-150',
                      active
                        ? 'bg-primary/15 text-primary'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    <Icon size={13} />
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-wide">{label}</span>
                </button>
              );
            })}
          </nav>

          {/* Active section content — min-h prevents nav jump; key replays the
              fade-in animation each time the section changes */}
          <div key={section} className="min-w-0 space-y-4 animate-fade-in md:min-h-[480px]">

            {/* ── APPEARANCE ── */}
            {section === 'appearance' && (
              <>
                <SettingsPanel label={t('settings.language')}>
                  <SegGroup>
                    <SegOption
                      active={i18n.language === 'en'}
                      onClick={() => handleLanguageChange('en')}
                    >
                      English
                    </SegOption>
                    <SegOption
                      active={i18n.language === 'pt-BR'}
                      onClick={() => handleLanguageChange('pt-BR')}
                    >
                      Português
                    </SegOption>
                  </SegGroup>
                </SettingsPanel>

                <SettingsPanel label={t('settings.theme')}>
                  <SegGroup>
                    {themeOptions.map(({ value, icon: Icon, label }) => (
                      <SegOption
                        key={value}
                        active={settings.theme === value}
                        onClick={() => setTheme(value)}
                      >
                        <Icon size={13} />
                        {label}
                      </SegOption>
                    ))}
                  </SegGroup>
                </SettingsPanel>
              </>
            )}

            {/* ── EDITOR ── */}
            {section === 'editor' && (
              <SettingsPanel label={t('settings.editor')}>
                <div className="space-y-6">

                  {/* Font family */}
                  <div>
                    <FieldLabel>{t('settings.fontFamily')}</FieldLabel>
                    <SegGroup>
                      {(['serif', 'sans', 'mono'] as const).map((font) => (
                        <SegOption
                          key={font}
                          active={settings.fontFamily === font}
                          onClick={() => updateSettings({ fontFamily: font })}
                          className={cn(
                            font === 'serif' && 'font-serif',
                            font === 'mono' && 'font-mono',
                            font === 'sans' && 'font-sans'
                          )}
                        >
                          {t(`settings.${font}`)}
                        </SegOption>
                      ))}
                    </SegGroup>
                  </div>

                  {/* Font size slider */}
                  <div>
                    <FieldLabel>
                      {t('settings.fontSize')}:{' '}
                      <span className="text-primary">{settings.fontSize}px</span>
                    </FieldLabel>
                    <Slider
                      min={12}
                      max={24}
                      value={[settings.fontSize]}
                      onValueChange={(value) => {
                        const v = Array.isArray(value) ? value[0] : value;
                        updateSettings({ fontSize: v });
                      }}
                    />
                  </div>

                  {/* Line height */}
                  <div>
                    <FieldLabel>{t('settings.lineHeight')}</FieldLabel>
                    <SegGroup>
                      {(['compact', 'normal', 'relaxed'] as const).map((height) => (
                        <SegOption
                          key={height}
                          active={settings.lineHeight === height}
                          onClick={() => updateSettings({ lineHeight: height })}
                        >
                          {t(`settings.${height}`)}
                        </SegOption>
                      ))}
                    </SegGroup>
                  </div>

                  {/* Toggles list */}
                  <div className="border-t border-border pt-4">
                    <FieldLabel>{t('settings.behavior', 'Comportamento')}</FieldLabel>
                    <div className="divide-y divide-border/60">
                      <SettingsToggle
                        label={t('settings.showWordCount')}
                        checked={settings.showWordCount}
                        onChange={(checked) => updateSettings({ showWordCount: checked })}
                      />
                      <SettingsToggle
                        label={t('settings.showCharCount')}
                        checked={settings.showCharCount}
                        onChange={(checked) => updateSettings({ showCharCount: checked })}
                      />
                      <SettingsToggle
                        label={t('settings.typewriterMode')}
                        description={t('settings.typewriterModeDesc')}
                        checked={settings.typewriterMode}
                        onChange={(checked) => updateSettings({ typewriterMode: checked })}
                      />
                      <SettingsToggle
                        label={t('settings.spellcheck')}
                        checked={settings.spellcheck}
                        onChange={(checked) => updateSettings({ spellcheck: checked })}
                      />
                      <SettingsToggle
                        label={t('settings.showChapterNumber', 'Mostrar Número do Capítulo')}
                        description={t('settings.showChapterNumberDesc', 'Exibir o número do capítulo acima do título')}
                        checked={settings.showChapterNumber}
                        onChange={(checked) => updateSettings({ showChapterNumber: checked })}
                      />
                      <SettingsToggle
                        label={t('settings.dropCaps', 'Capitular (letra inicial)')}
                        description={t('settings.dropCapsDesc', 'Aumenta a primeira letra de cada capítulo, descendo sobre as primeiras linhas')}
                        checked={settings.dropCaps}
                        onChange={(checked) => updateSettings({ dropCaps: checked })}
                      />
                    </div>
                  </div>

                </div>
              </SettingsPanel>
            )}

            {/* ── AUTOSAVE ── */}
            {section === 'autosave' && (
              <SettingsPanel label={t('settings.autoSave')}>
                <div className="space-y-4">
                  <SettingsToggle
                    label={t('settings.autoSave')}
                    checked={settings.autoSave}
                    onChange={(checked) => updateSettings({ autoSave: checked })}
                  />
                  {settings.autoSave && (
                    <div className="border-t border-border pt-4">
                      <FieldLabel>
                        {t('settings.autoSaveInterval')}:{' '}
                        <span className="text-primary">
                          {settings.autoSaveInterval} {t('settings.seconds')}
                        </span>
                      </FieldLabel>
                      <Slider
                        min={10}
                        max={120}
                        step={10}
                        value={[settings.autoSaveInterval]}
                        onValueChange={(value) => {
                          const v = Array.isArray(value) ? value[0] : value;
                          updateSettings({ autoSaveInterval: v });
                        }}
                      />
                    </div>
                  )}
                </div>
              </SettingsPanel>
            )}

            {/* ── SHORTCUTS ── */}
            {section === 'shortcuts' && (
              <SettingsPanel label={t('settings.keyboard')}>
                <div className="grid gap-x-8 gap-y-0.5 sm:grid-cols-2">
                  <ShortcutRow keys={['Ctrl', 'S']} action={t('shortcuts.save')} index={0} />
                  <ShortcutRow keys={['Ctrl', 'Z']} action={t('shortcuts.undo')} index={1} />
                  <ShortcutRow keys={['Ctrl', 'Y']} action={t('shortcuts.redo')} index={2} />
                  <ShortcutRow keys={['Ctrl', 'B']} action={t('shortcuts.bold')} index={3} />
                  <ShortcutRow keys={['Ctrl', 'I']} action={t('shortcuts.italic')} index={4} />
                  <ShortcutRow keys={['Ctrl', 'U']} action={t('shortcuts.underline')} index={5} />
                  <ShortcutRow keys={['Ctrl', 'F']} action={t('shortcuts.find')} index={6} />
                  <ShortcutRow keys={['F11']} action={t('shortcuts.fullscreen')} index={7} />
                </div>
              </SettingsPanel>
            )}

            {/* ── DATA ── */}
            {section === 'data' && (
              <SettingsPanel label={t('settings.backup')}>
                <div className="space-y-4">

                  {/* Export / Import */}
                  <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                    <FieldLabel>{t('settings.backupRestore', 'Backup & Restauração')}</FieldLabel>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 rounded-lg font-mono text-xs transition-colors"
                        onClick={handleExportData}
                      >
                        <Download size={14} />
                        {t('settings.exportData')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 rounded-lg font-mono text-xs transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload size={14} />
                        {t('settings.importData')}
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json"
                        onChange={handleImportData}
                        className="hidden"
                      />
                    </div>
                  </div>

                  {/* Danger zone */}
                  <div className="rounded-xl border border-destructive/25 bg-destructive/[0.06] p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Trash2 size={13} className="text-destructive/70" />
                      <span className="font-mono text-[7.5px] uppercase tracking-[0.2em] text-destructive/80">
                        {t('settings.dangerZone', 'Zona de Perigo')}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('settings.clearDataDesc', 'Remove permanentemente todos os dados locais.')}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        'gap-2 rounded-lg border-destructive/30 font-mono text-xs text-destructive',
                        'transition-colors duration-150',
                        'hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive',
                        'focus-visible:ring-2 focus-visible:ring-destructive/40'
                      )}
                      onClick={handleClearData}
                    >
                      <Trash2 size={14} />
                      {t('settings.clearData')}
                    </Button>
                  </div>

                  {/* Privacy notice */}
                  <div className="flex gap-2.5 rounded-xl border border-border bg-muted/20 p-3.5 text-muted-foreground">
                    <Info size={15} className="mt-0.5 shrink-0 text-primary/60" />
                    <p className="text-xs leading-relaxed">
                      {t(
                        'settings.privacyNote',
                        'Todos os dados são armazenados localmente no seu dispositivo.'
                      )}
                    </p>
                  </div>

                </div>
              </SettingsPanel>
            )}

            {/* ── INTEGRATIONS (BYOK) ── */}
            {section === 'integrations' && (
              <SettingsPanel label={t('settingsView.integrations', 'Integrações')}>
                <div className="space-y-5">

                  {/* Status banner */}
                  {user?.hasOwnKey && (
                    <div className="flex items-start gap-3 rounded-xl border border-green-500/30 bg-green-500/[0.06] p-3.5">
                      <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-green-500" />
                      <div className="space-y-0.5">
                        <p className="text-sm font-medium text-green-700 dark:text-green-400">
                          {t('settingsView.byokActiveBanner', 'Usando sua própria chave — sem limites de cota')}
                        </p>
                        {user.keyMaskedSuffix && (
                          <p className="font-mono text-xs text-muted-foreground">
                            {t('settingsView.keyEndingIn', 'Chave terminando em')} <span className="text-foreground">…{user.keyMaskedSuffix}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div className="space-y-2">
                    <FieldLabel>{t('settingsView.byokKeyLabel', 'Chave da OpenAI (opcional)')}</FieldLabel>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t(
                        'settingsView.byokDescription',
                        'Conecte sua própria chave da OpenAI para usar a IA sem limites mensais de ações. O custo das chamadas vai direto para sua conta OpenAI, e sua cota do plano é ignorada.'
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground/70">
                      {t('settingsView.byokEncryptedNote', 'A chave é armazenada criptografada (AES-256-GCM) e nunca é exibida em texto puro.')}
                    </p>
                  </div>

                  {/* Key input */}
                  <div className="space-y-3">
                    <div className="relative flex items-center gap-2">
                      <input
                        type={byokShowKey ? 'text' : 'password'}
                        value={byokKey}
                        onChange={(e) => setByokKey(e.target.value)}
                        placeholder={user?.hasOwnKey ? t('settingsView.byokReplacePlaceholder', 'Digite uma nova chave para substituir…') : 'sk-…'}
                        className={cn(
                          'w-full rounded-[11px] bg-card ring-1 ring-inset ring-border px-3 py-2.5 pr-10',
                          'font-mono text-sm placeholder:text-muted-foreground/50',
                          'focus:outline-none focus:ring-2 focus:ring-ring/60 focus:ring-offset-1',
                          'transition-colors duration-150'
                        )}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && byokKey.trim()) void handleSaveByokKey();
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setByokShowKey((v) => !v)}
                        className="absolute right-3 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={byokShowKey ? t('settingsView.hideKey', 'Ocultar chave') : t('settingsView.showKey', 'Mostrar chave')}
                      >
                        {byokShowKey ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        className="gap-2 rounded-lg font-mono text-xs"
                        disabled={!byokKey.trim() || byokSaving}
                        onClick={handleSaveByokKey}
                      >
                        <Key size={13} />
                        {byokSaving
                          ? t('settingsView.saving', 'Salvando…')
                          : (user?.hasOwnKey ? t('settingsView.updateKey', 'Atualizar chave') : t('settingsView.saveKey', 'Salvar chave'))}
                      </Button>

                      {user?.hasOwnKey && (
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            'gap-2 rounded-lg border-destructive/30 font-mono text-xs text-destructive',
                            'hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive'
                          )}
                          disabled={byokRemoving}
                          onClick={handleRemoveByokKey}
                        >
                          <Trash2 size={13} />
                          {byokRemoving ? t('settingsView.removing', 'Removendo…') : t('settingsView.removeKey', 'Remover chave')}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Privacy note */}
                  <div className="flex gap-2.5 rounded-xl border border-border bg-muted/20 p-3.5 text-muted-foreground">
                    <Info size={15} className="mt-0.5 shrink-0 text-primary/60" />
                    <p className="text-xs leading-relaxed">
                      {t(
                        'settingsView.byokPrivacyNote',
                        'Sua chave é enviada via HTTPS, criptografada antes de ser salva no banco de dados, e nunca exposta em logs ou respostas da API. Você pode removê-la a qualquer momento.'
                      )}
                    </p>
                  </div>

                </div>
              </SettingsPanel>
            )}

            {/* ── ABOUT ── */}
            {section === 'about' && (
              <SettingsPanel label={t('settings.about')}>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
                      <span className="font-serif text-xl font-bold text-primary">A</span>
                    </div>
                    <div className="space-y-1">
                      <p className="font-serif text-base font-semibold text-foreground">
                        {t('app.name')}
                      </p>
                      <p className="text-sm text-muted-foreground">{t('app.tagline')}</p>
                      <p className="font-mono text-xs text-muted-foreground/70">
                        {t('settings.version')}{' '}
                        <span className="text-foreground/60">1.0.0</span>
                      </p>
                    </div>
                  </div>
                </div>
              </SettingsPanel>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ── */

/** Card grouper — mock ".dcard": rounded card, translucent bg, small mono label header. */
function SettingsPanel({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card/60 p-5 md:p-6 space-y-4">
      <h2 className="font-mono text-[8.5px] font-semibold uppercase tracking-[0.26em] text-muted-foreground">
        {label}
      </h2>
      {children}
    </div>
  );
}

/** Small field label — mock ".sh-l": mono, tiny, wide tracking, muted. */
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-2 block font-mono text-[7.5px] uppercase tracking-[0.2em] text-muted-foreground">
      {children}
    </label>
  );
}

/** Segmented control wrapper — mock ".seg". */
function SegGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-1 rounded-[9px] bg-foreground/5 p-[3px]">
      {children}
    </div>
  );
}

/** Segmented control option — mock ".seg span" / ".seg span.on". */
function SegOption({
  active,
  onClick,
  children,
  className = '',
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-1 min-w-[64px] items-center justify-center gap-1.5 rounded-[7px] px-3 py-2',
        'text-[10.5px] font-medium transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60',
        'active:scale-[0.97]',
        active
          ? 'bg-popover text-foreground shadow-sm ring-1 ring-inset ring-border'
          : 'text-muted-foreground hover:text-foreground',
        className
      )}
    >
      {children}
    </button>
  );
}

/** Toggle row — mock ".tgl-row": flat row, no card chrome; Switch stays the real themed component. */
function SettingsToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <span className="text-[13px] text-foreground">{label}</span>
        {description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function ShortcutRow({ keys, action, index }: { keys: string[]; action: string; index: number }) {
  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-lg px-3 py-2.5',
        'transition-colors duration-150 hover:bg-muted/40',
        index % 2 === 0 ? 'bg-transparent' : 'bg-muted/10'
      )}
    >
      <span className="text-xs text-muted-foreground">{action}</span>
      <div className="flex gap-1">
        {keys.map((key, i) => (
          <kbd
            key={i}
            className="rounded-md border border-border bg-background px-2 py-1 font-mono text-[10px] text-foreground shadow-sm"
          >
            {key}
          </kbd>
        ))}
      </div>
    </div>
  );
}
